import React, {
  useState,
  useImperativeHandle,
  forwardRef,
  useRef,
  useEffect,
  useMemo,
} from "react";
import { CustomCellEditorProps } from "ag-grid-react";
import { Popover, Box, Checkbox, FormControlLabel, Stack } from "@mui/material";
import { Button, Typography } from "@platform/design-system";

import { BenefitClass } from "../../../../types/rfp.api.types";

export interface EligibilityClassesEditorRef {
  getValue: () => BenefitClass[];
}

export interface EligibilityClassesEditorParams {
  availableClasses: BenefitClass[];
}

/**
 * Custom cell editor for eligibility classes multi-select
 * Displays checkboxes for all available benefit classes.
 * Shows an error-colored helper text when the field is required and no classes are selected.
 */
export const EligibilityClassesEditor = forwardRef<
  EligibilityClassesEditorRef,
  CustomCellEditorProps
>((props, ref) => {
  const { stopEditing, api, node, column } = props;
  const value = Array.isArray(props.value) ? props.value : [];
  const isRequired = props.data?.benefit?.isRequired === true;

  // Get available classes from cellEditorParams
  const editorParams =
    (props as unknown as Record<string, unknown>)["cellEditorParams"] ??
    (props as unknown);
  const params = editorParams as EligibilityClassesEditorParams;
  const rawAvailableClasses = params?.availableClasses;
  const availableClasses = useMemo(
    () => rawAvailableClasses || [],
    [rawAvailableClasses],
  );

  // Initialize selected class IDs from value
  const initialSelectedIds = new Set(
    value.map((item: BenefitClass) => item.id),
  );
  const [selectedIds, setSelectedIds] =
    useState<Set<string>>(initialSelectedIds);

  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  // Expose getValue method to AG Grid
  useImperativeHandle(
    ref,
    () => ({
      getValue: () => availableClasses.filter((cls) => selectedIds.has(cls.id)),
    }),
    [availableClasses, selectedIds],
  );

  // Auto-open popover when editor is created
  useEffect(() => {
    const timer = setTimeout(() => {
      setOpen(true);
    }, 10);
    return () => clearTimeout(timer);
  }, []);

  const handleClose = (): void => {
    setOpen(false);
    if (stopEditing) {
      setTimeout(() => {
        // Cancel the edit when popover is dismissed without clicking Done
        stopEditing(true);
      }, 0);
    }
  };

  const handleDone = (): void => {
    const finalValue = availableClasses.filter((cls) =>
      selectedIds.has(cls.id),
    );

    // Set the data value directly, then stop editing in commit (non-cancel)
    // mode — matching the pattern used by PopoverSelectEditor and other
    // popover editors in this directory, so the edit lifecycle completes
    // the same way across all of them.
    if (node && column) {
      const columnField = column.getColId();
      node.setDataValue(columnField, finalValue);
    }

    if (api) {
      api.stopEditing();
    } else if (stopEditing) {
      stopEditing();
    }

    // Force refresh the cell to update the display
    if (api && node) {
      setTimeout(() => {
        api.refreshCells({ rowNodes: [node], force: true });
      }, 0);
    }

    setOpen(false);
  };

  const handleToggle = (classId: string): void => {
    setSelectedIds((prev) => {
      const updated = new Set(prev);
      if (updated.has(classId)) {
        updated.delete(classId);
      } else {
        updated.add(classId);
      }
      return updated;
    });
  };

  return (
    <>
      <div ref={anchorRef} style={{ width: "100%", height: "100%" }} />
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        disableEnforceFocus
        disableAutoFocus={false}
        disableRestoreFocus
        slotProps={{
          paper: {
            sx: {
              minWidth: 300,
              maxWidth: 400,
              maxHeight: 500,
              overflow: "auto",
            },
          },
        }}
      >
        <Box
          sx={{ p: 2 }}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              event.stopPropagation();
              handleDone();
            }
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 2 }} color="textSecondary">
            Select Eligibility Classes
            {isRequired && (
              <Typography
                component="span"
                variant="subtitle2"
                sx={{ ml: 0.5 }}
                color="danger"
              >
                *
              </Typography>
            )}
          </Typography>

          {isRequired && selectedIds.size === 0 && (
            <Typography
              variant="caption"
              sx={{ display: "block", mb: 1 }}
              color="danger"
            >
              At least one class is required
            </Typography>
          )}

          {availableClasses.length === 0 ? (
            <Typography variant="body-medium" color="textSecondary">
              No benefit classes available
            </Typography>
          ) : (
            <Stack spacing={1}>
              {availableClasses.map((cls) => (
                <FormControlLabel
                  key={cls.id}
                  control={
                    <Checkbox
                      checked={selectedIds.has(cls.id)}
                      onChange={() => handleToggle(cls.id)}
                    />
                  }
                  label={cls.name}
                />
              ))}
            </Stack>
          )}

          <Box
            sx={{ mt: 2, display: "flex", justifyContent: "flex-end", gap: 1 }}
          >
            <Button onClick={handleDone} variant="primary">
              Done
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
});

EligibilityClassesEditor.displayName = "EligibilityClassesEditor";
